<template>
  <v-dialog
    v-model="dialog"
    max-width="850"
    persistent
    :fullscreen="$vuetify.breakpoint.xs"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        small
        v-on="on"
        v-bind="attrs"
        :color="creatingFreespin ? 'primary' : ''"
        :disabled="update && (freespin.status == 0 || freespin.status == 2)"
      >
        <v-icon :left="isCreating">
          {{ isCreating ? "mdi-plus-box" : view ? "mdi-eye" : "mdi-pencil" }}
        </v-icon>
        <span v-if="isCreating">
          {{ $t("freespins.createCampaign") }}
        </span>
      </v-btn>
    </template>

    <v-card>
      <v-form ref="form" v-model="form.valid" @submit="createOrUpdateFreespin">
        <v-toolbar dense>
          <span v-if="update">
            {{ $t("freespins.update") }}
          </span>
          <span v-else-if="view">
            {{ $t("freespins.view") }}
          </span>
          <span v-else>
            {{ $t("freespins.create") }}
          </span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeDialog()">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text>
          <v-subheader class="pl-0 overline" v-if="view === false">
            {{ computedTitleCreateOrUpdateCampaign }}
            <v-divider class="ml-4"></v-divider>
          </v-subheader>
          <v-row>
            <v-col class="pb-0" cols="12" xl="6" md="6" sm="6">
              <div class="mb-2">
                {{ $t("freespins.form.campaignTitle") }}
              </div>
              <v-text-field
                v-model="form.campaignTitle"
                :rules="[rules.required, rules.notEmpty, rules.max20]"
                :disabled="view"
                dense
                :hide-details="isCreating || update ? 'auto' : true"
                counter="20"
                maxlength="20"
                outlined
                clearable
              />
            </v-col>

            <v-col class="pb-0" cols="12">
              <div class="mb-2">
                {{ $t("description") }}
              </div>
              <v-textarea
                v-model="form.description"
                :rules="[rules.required, rules.notEmpty, rules.max50]"
                :disabled="view"
                counter="50"
                maxlength="50"
                dense
                :hide-details="isCreating || update ? 'auto' : true"
                rows="2"
                no-resize
                outlined
                clearable
              />
            </v-col>

            <v-col class="py-0" cols="12">
              <v-subheader class="pl-0 overline">
                {{ $t("freespins.form.subheaders.campaignConfigurations") }}
                <v-divider class="ml-4"></v-divider>
              </v-subheader>
            </v-col>
            <!-- Campaign Type -->
            <v-col class="pt-0" cols="12" xl="6" md="6" sm="6">
              <div class="mb-2">
                {{ $t("freespins.form.campaignType") }}
              </div>
              <v-select
                v-model="form.campaignType"
                :items="options.campaignTypes"
                :hide-details="update || view ? true : 'auto'"
                :disabled="update || view"
                dense
                outlined
              />
            </v-col>
            <!-- Campaign Hide -->
            <v-col
              :class="{
                'pa-0': !isCampaignTypeDeposit,
                'pb-0': isCampaignTypeDeposit && $vuetify.breakpoint.xs,
                'pt-0': isCampaignTypeDeposit && !$vuetify.breakpoint.xs,
              }"
              cols="12"
              xl="6"
              md="6"
              sm="6"
            >
              <!-- Visibilidade de campanha -->
              <div v-if="isCampaignTypeDeposit">
                {{ $t("freespins.form.campaignVisibility.title") }}
                <v-tooltip top content-class="tooltip">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      v-on="on"
                      v-bind="attrs"
                      class="mb-1 ml-2"
                      color="primary"
                      small
                    >
                      mdi-information-outline
                    </v-icon>
                  </template>
                  <span>
                    {{
                      $t("freespins.form.campaignVisibility.tooltip", {
                        updateMessage: $t(
                          "freespins.form.campaignVisibility.tooltipUpdateMessage"
                        ),
                      })
                    }}
                  </span>
                </v-tooltip>
              </div>
              <v-switch
                v-if="isCampaignTypeDeposit"
                v-model="form.campaignHide"
                class="mt-3"
                :label="campaignHideLabel"
                :disabled="view"
              ></v-switch>
            </v-col>

            <!-- Rollover Progressivo Divider Select -->
            <v-col
              v-if="isCampaignTypeDeposit"
              class="pb-0"
              cols="12"
              xl="6"
              md="6"
              sm="6"
            >
              {{ $t("freespins.progressiveSpins") }}
              <v-tooltip top content-class="tooltip">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    v-on="on"
                    v-bind="attrs"
                    class="mb-1 ml-2"
                    color="primary"
                    small
                  >
                    mdi-information-outline
                  </v-icon>
                </template>
                <span v-html="$t('freespins.progressiveSpinsInfo')"></span>
              </v-tooltip>

              <v-select
                v-model="form.selectFreespinsProgress"
                :items="form.freespinsProgressItems"
                return-object
                height="32px"
                class="mt-2"
                outlined
                dense
                hide-details
                :disabled="update || view"
              >
                <template v-slot:selection="{ item }">
                  {{ item.text }}
                </template>
              </v-select>
            </v-col>

            <!-- Campaign Code -->
            <v-col
              class="pb-0"
              cols="12"
              xl="6"
              md="6"
              sm="6"
              :class="{ 'pa-0': !isCampaignTypeDeposit }"
            >
              <div v-if="isCampaignTypeDeposit" class="mb-2">
                {{ $t("freespins.form.campaignCode") }}
                <v-tooltip top content-class="tooltip">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      v-on="on"
                      v-bind="attrs"
                      class="mb-1 ml-2"
                      color="primary"
                      small
                    >
                      mdi-information-outline
                    </v-icon>
                  </template>
                  <span>{{ $t("freespins.form.campaignCodeTip") }}</span>
                </v-tooltip>
              </div>
              <v-text-field
                v-if="isCampaignTypeDeposit"
                v-model="form.campaignCode"
                :rules="[
                  rules.noSpacesAreAllowed,
                  rules.required,
                  rules.max20,
                  rules.noSpecialCharactersAllowed
                ]"
                :hide-details="update || view ? true : 'auto'"
                :disabled="update || view"
                :counter="20"
                maxlength="20"
                dense
                outlined
                clearable
              ></v-text-field>
            </v-col>
            <!-- Minimum Deposit -->
            <v-col
              class="pb-0"
              cols="12"
              xl="6"
              md="6"
              sm="6"
              :class="{ 'pa-0': !isCampaignTypeDeposit }"
            >
              <div v-if="isCampaignTypeDeposit" class="mb-2">
                {{ $t("minimumDeposit") }}
                <v-tooltip top content-class="tooltip">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      v-on="on"
                      v-bind="attrs"
                      class="mb-1 ml-2"
                      color="primary"
                      small
                    >
                      mdi-information-outline
                    </v-icon>
                  </template>
                  <span>
                    {{ $t("freespins.form.campaignMinimumDepositTip") }}
                  </span>
                </v-tooltip>
              </div>
              <v-text-field
                v-if="isCampaignTypeDeposit"
                v-model="form.minDeposit"
                :rules="
                  isCampaignTypeDeposit && [rules.required, rules.negativeNo]
                "
                :hide-details="update || view ? true : 'auto'"
                :disabled="update || view"
                dense
                outlined
                type="number"
              ></v-text-field>
            </v-col>
            <!-- Users who can claim -->
            <v-col v-if="form.campaignType == 'personalizado'" cols="12">
              <div class="mb-2">
                {{ $t("freespins.form.users") }}
              </div>

              <v-select
                v-model="form.players"
                :items="options.players"
                :rules="update || form.players === 0 ? [] : [rules.required]"
                :disabled="update || view"
                dense
                hide-no-data
                hide-details="auto"
                outlined
              />
            </v-col>
            <!-- CSV Upload -->
            <v-row v-if="form.campaignType == 'personalizado' && form.players === 5"
              style="width: 100%" class="pb-3 px-3"
              no-gutters
            >
              <v-col cols="12" sm="7" md="6">
                <div class="mb-2">
                  {{ $t("freespins.form.doCSVUpload") }}
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs}">
                      <v-icon
                        color="info"
                        v-bind="attrs"
                        v-on="on"
                        small
                      >
                        mdi-information-outline
                      </v-icon>
                    </template>
                    <span>{{ $t("freespins.form.CSVUploadTip") }}</span>
                  </v-tooltip>
                </div>
                <div class="d-flex align-top" style="gap: 16px">
                  <v-file-input
                    class="d-flex flex-grow"
                    accept=".csv"
                    :label="$t('freespins.form.inputCsvLabel')"
                    dense
                    outlined
                    :error="form.csv.fileError"
                    :rules="[rules.required]"
                    hide-details="auto"
                    show-size
                    clearable
                    @click:clear="clearCsvInput()"
                    @change="handleCSVUpload($event)"
                  >
                    <template v-slot:selection>
                      <div v-if="form.csv.file" class="caption text-truncate">
                        <span class="">{{ form.csv.file.name }}</span>
                        <span class="ml-1">({{ form.csv.file.size }} Bytes)</span>
                      </div>
                    </template>
                  </v-file-input>
                  <v-btn
                    @click="form.csv.seeCsvUsers = true"
                    class="info"
                    :disabled="!form.csv.parsed"
                    >
                  {{ $t("freespins.form.see") }}
                  </v-btn>

                  <v-dialog
                    v-model="form.csv.seeCsvUsers"
                    max-width="600"
                  >
                    <v-toolbar
                      dense
                    >
                      <v-toolbar-title class="d-flex align-center">{{ $t("users") }} <span class="caption ml-2">({{ form.csv.content.length }})</span></v-toolbar-title>
                      <v-spacer></v-spacer>
                      <v-btn @click="form.csv.seeCsvUsers = false" icon><v-icon>mdi-close</v-icon></v-btn>
                    </v-toolbar>
                    <v-card>
                      <div class="py-8 mx-auto" style="width: 90%">
                        <v-row no-gutters style="row-gap: 8px;">
                          <v-col
                            v-for="(user, index) in form.csv.content"
                            :key="index"
                            cols="12"
                            md="6"
                            lg="6"
                            sm="6"
                            xl="6"
                          >
                            <div v-if="user.id">
                              ID: {{ user.id }}
                              <div v-if="user.Nome" class="grey--text">
                                {{ user.Nome }}
                              </div>
                            </div>
                          </v-col>
                        </v-row>
                      </div>
                    </v-card>
                  </v-dialog>
                </div>
              </v-col>
            </v-row>

            <!-- Search Player -->
            <v-col
              v-if="form.campaignType == 'personalizado' && form.players === 0"
              cols="12"
            >
              <div
                class="d-flex flex-column align-center justify-space-between"
              >
                <div class="mb-2 align-self-start">
                  {{ $t("freespins.form.manualUsers") }}
                </div>
                <div class="mb-2 align-self-end">
                  <SelectedPlayersDetail
                    :selectedPlayers="form.selectedPlayers"
                  />
                </div>
              </div>
              <!-- eh eu -->
              <v-autocomplete
                v-model="form.selectedPlayers"
                :search-input.sync="form.playerSearch"
                :rules="update ? [] : [rules.requiredAutocomplete]"
                :readonly="update || view"
                :items="form.fetchedPlayers"
                :loading="loading.playerSearch"
                :no-data-text="$t('autoCompleteNoData')"
                ref="autoComplete"
                item-text="ui"
                item-value="id"
                hide-details="auto"
                style="padding: 0.1rem"
                return-object
                deletable-chips
                chips
                multiple
                dense
                outlined
                no-filter
              >
              </v-autocomplete>
            </v-col>
            <!-- Provider -->
            <v-col cols="12" xl="6" md="6" sm="6">
              <div class="mb-2">
                {{ $t("provider") }}
              </div>
              <v-autocomplete
                v-if="isCreating"
                v-model="form.provider"
                :items="options.providers"
                :rules="[rules.required]"
                :loading="loading.providers"
                item-text="provider_name"
                item-value="id"
                background-color="transparent"
                dense
                hide-no-data
                hide-details="auto"
                outlined
                :no-data-text="$t('autoCompleteNoData')"
                return-object
              >
              </v-autocomplete>

              <v-text-field
                v-else
                :value="form.provider.provider"
                disabled
                dense
                hide-details="auto"
                outlined
                return-object
              />
            </v-col>
            <!-- Game -->
            <v-col cols="12" xl="6" md="6" sm="6">
              <div class="mb-2">
                {{ $t("freespins.form.game") }}
              </div>
              <!-- aqui -->
              <v-autocomplete
                v-if="isCreating"
                v-model="form.games"
                :items="gamesByProviders"
                :loading="loading.games"
                :rules="!!gameHint ? [] : [rules.selectRequired]"
                :disabled="!!gameHint"
                :hint="gameHint"
                :no-data-text="$t('autoCompleteNoData')"
                item-text="title"
                multiple
                dense
                persistent-hint
                hide-details="auto"
                hide-no-data
                outlined
                return-object
                :counter="configOneOrManyGamesByProviders"
                @change="limitSelectedGames"
              >
                <template v-slot:item="{ item }">
                  <v-list-item-avatar>
                    <v-img :src="item.src"></v-img>
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title v-text="item.title"></v-list-item-title>
                  </v-list-item-content>
                </template>

                <template v-slot:selection="{ item, index }">
                  <v-chip small v-if="index === 0">
                    <span>
                      {{ item.title }}
                    </span>
                  </v-chip>
                  <span v-if="index === 1" class="grey--text text-caption">
                    (+{{ form.games.length - 1 }} {{ $t("others") }})
                  </span>
                </template>
              </v-autocomplete>

              <div v-else class="d-flex align-center" style="height: 40px;">
                <SeeGames :games="freespin.games" />
              </div>
            </v-col>
            <!-- Rounds -->
            <v-col cols="12" xl="6" md="6" sm="6">
              <div class="mb-2">
                {{ $t("freespins.form.rounds") }}
              </div>
              <v-text-field
                v-model="form.rounds"
                :min="1"
                :max="100"
                type="number"
                onkeypress="return event.keyCode === 8 || event.charCode >= 48 && event.charCode <= 57"
                single-line
                :hint="$t('freespins.form.roundsHint')"
                :rules="
                  update || view
                    ? []
                    : [rules.required, rules.minRounds, rules.maxRounds]
                "
                :disabled="update || view"
                dense
                hide-details="auto"
                outlined
                clearable
              >
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title
                      v-text="item.provider"
                    ></v-list-item-title>
                  </v-list-item-content>
                </template>
              </v-text-field>
            </v-col>
            <!-- Days To Expires -->
            <v-col cols="12" xl="6" md="6" sm="6">
              <div class="mb-2">
                {{ $t("freespins.form.daysToExpire") }}
              </div>

              <v-text-field
                v-model="form.daysToExpire"
                :min="1"
                :max="30"
                type="number"
                onkeypress="return event.keyCode === 8 || event.charCode >= 48 && event.charCode <= 57"
                single-line
                :rules="
                  update || view
                    ? []
                    : [
                        rules.required,
                        rules.negativeNo,
                        rules.minDaysToExpire,
                        rules.max30DaysToExpire,
                      ]
                "
                :disabled="update || view"
                dense
                hide-details="auto"
                outlined
                clearable
              />
            </v-col>

            <!-- Multiplicador do vínculo depósito -->
            <!-- <v-col cols="12" xl="6" md="6" sm="6" v-if="hasDepositBond"> -->
            <v-col cols="12" xl="6" md="6" sm="6" v-if="isCampaignTypeDeposit">
              <div class="mb-2 d-flex align-center" style="gap: 8px;">
                <div>
                  {{ $t("rolloverMultiplier2") }}
                </div>

                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-on="on" v-bind="attrs" small color="primary">
                      mdi-information-outline
                    </v-icon>
                  </template>
                  <span
                    v-html="$t('freespins.form.freeSpinsRolloverInfo')"
                  >
                  </span>
                </v-tooltip>
              </div>

              <v-text-field
                v-model="form.multiplierBondDeposit"
                :min="1"
                :max="100"
                type="number"
                single-line
                onkeypress="return event.keyCode === 8 || event.charCode >= 48 && event.charCode <= 57"
                :rules="
                  update || view
                    ? []
                    : [
                        rules.required,
                        rules.negativeNo,
                        rules.minDepositBondMultiplier,
                        rules.maxDepositBondMultiplier,
                      ]
                "
                :disabled="update || view"
                dense
                hide-details="auto"
                outlined
                clearable
              />
            </v-col>

            <!-- Start Date - Start Time - End Date - End Time -->
            <div class="px-3 pt-4" style="width: 100%">
              <v-row>
                <v-col>
                  <div class="mb-2">
                    {{ $t("freespins.form.startDate") }}
                  </div>
                  <v-menu
                    v-model="menus.start"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    min-width="200"
                    offset-y
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="formattedStartDate"
                        outlined
                        prepend-icon="mdi-calendar"
                        readonly
                        hint="DD/MM/YYYY"
                        hide-details="auto"
                        :disabled="update || view"
                        v-on="on"
                        dense
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="form.dates.start"
                      @input="menus.start = false"
                      :min="today"
                      no-title
                      scrollable
                    ></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col>
                  <div class="mb-2">
                    {{ $t("freespins.form.startTime") }}
                  </div>
                  <v-text-field
                    v-model="form.dates.timeStart"
                    :rules="update || view ? [] : [rules.required]"
                    prepend-icon="mdi-clock-time-twelve"
                    placeholder="hh:mm"
                    dense
                    outlined
                    :disabled="update || view"
                    v-mask="'##:##'"
                    hide-details="auto"
                    :error="errors.timeStart.status"
                    :error-messages="errors.timeStart.message"
                  >
                  </v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <div class="mb-2">
                    {{ $t("freespins.form.endDate") }}
                  </div>
                  <v-menu
                    v-model="menus.final"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="200"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="formattedFinalDate"
                        outlined
                        prepend-icon="mdi-calendar"
                        readonly
                        hint="DD/MM/YYYY"
                        :disabled="update || view"
                        hide-details="auto"
                        v-on="on"
                        dense
                        :rules="[rules.required]"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="form.dates.final"
                      @input="menus.final = false"
                      :min="form.dates.start || today"
                      no-title
                      scrollable
                    ></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col>
                  <div class="mb-2">
                    {{ $t("freespins.form.endTime") }}
                  </div>
                  <v-text-field
                    v-model="form.dates.timeFinal"
                    :rules="update || view ? [] : [rules.required]"
                    placeholder="hh:mm"
                    prepend-icon="mdi-clock-time-eleven"
                    :disabled="update || view"
                    v-mask="'##:##'"
                    hide-details="auto"
                    dense
                    outlined
                    :error="errors.timeFinal.status"
                    :error-messages="errors.timeFinal.message"
                  >
                  </v-text-field>
                </v-col>
              </v-row>
            </div>
          </v-row>

          <!-- Upload Image -->
          <ImagePromoUpload
            :promoOrCampaignCode="form.campaignCode"
            :dialog="dialog"
            :valid="form.valid"
            :promotionType="form.campaignType"
            productType="casino"
            :isCreating="isCreating"
            @isImageSent="isImageSent"
            @campaignCodeImgCustom="campaignCodeImgCustom"
          />
        </v-card-text>

        <v-card-actions v-if="view === false" class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            color="info"
            @click.prevent="createOrUpdateFreespin"
            :disabled="disableButton"
            :loading="loading.freespinCreation"
          >
            {{ $t("toSave") }}
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import { validate } from "@/util/validate.js";
import SelectedPlayersDetail from "./SelectedPlayersDetail";
import rules from "../generateFields/rules";
import functioning from "../generateFields/functioning";
import ImagePromoUpload from "@/components/ImagePromoUpload.vue";
import SeeGames from "./SeeGames.vue";

export default {
  props: {
    freespin: {
      type: Object,
    },
    update: {
      type: Boolean,
    },
    view: {
      type: Boolean,
    },
    fetchedPlayers: {
      type: Object,
    },
  },

  components: {
    SelectedPlayersDetail,
    ImagePromoUpload,
    SeeGames,
  },

  data: (vm) => ({
    imageSent: false,
    lastSearchedTerm: "",
    initialUpdatingFreespinState: {},
    dialog: false,
    form: {
      valid: false,
      campaignTitle: "",
      campaignCode: "",
      description: "",
      campaignType: 0,
      campaignHide: false,
      selectFreespinsProgress: "",
      freespinsProgressItems: [
        { text: vm.$t("disabled"), value: false },
        { text: "1", value: 1 },
        { text: "2", value: 2 },
        { text: "3", value: 3 },
        { text: "4", value: 4 }
      ],
      minDeposit: "",
      players: "",
      fetchedPlayers: [],
      selectedPlayers: [],
      playerSearch: "",
      games: [],
      arrGamesIds: [],
      rounds: "",
      provider: "",
      daysToExpire: "",
      dates: {
        start: vm.today,
        final: "",
        timeStart: vm.currentHourAndMinute,
        timeFinal: "23:59",
      },
      multiplierBondDeposit: 1,
      // CSV
      csv: {
        file: "",
        content: [],
        parsed: false,
        usersByCsv: [],
        seeCsvUsers: false,
        fileError: false
      }
    },
    options: {
      campaignTypes: [
        {
          text: vm.$t("freespins.campaignTypeOptions.custom"),
          value: "personalizado",
        },
        {
          text: vm.$t("freespins.campaignTypeOptions.deposit"),
          value: "deposito",
        },
        {
          text: vm.$t("freespins.campaignTypeOptions.firstDeposit"),
          value: "1ºdeposito",
        },
      ],
      players: [
        {
          text: vm.$t("freespins.playersOptions.addByIdOrName"),
          value: 0,
        },
        {
          text: vm.$t("freespins.playersOptions.addByCsv"),
          value: 5,
        },
        {
          text: vm.$t("freespins.playersOptions.newUsersLast30Days"),
          value: 1,
        },
        {
          text: vm.$t("freespins.playersOptions.activeLast30Days"),
          value: 2,
        },
        {
          text: vm.$t("freespins.playersOptions.usersWithDeposits"),
          value: 3,
        },
        {
          text: vm.$t("freespins.playersOptions.usersWithoutDeposits"),
          value: 4,
        },
      ],
      games: [],
      gamesByProviders: {},
      providers: [],
    },
    loading: {
      providers: false,
      games: false,
      freespinCreation: false,
      playerSearch: false,
    },
    menus: {
      start: false,
      final: false,
    },
    rules: {
      noSpacesAreAllowed: validate.noSpacesAreAllowed,
      negativeNo: validate.negativeNo,
      required: validate.required,
      requiredAutocomplete: validate.requiredAutocomplete,
      selectRequired: validate.selectRequired,
      max20: validate.max20,
      max50: validate.max50Characters,
      minRounds: validate.minRounds,
      maxRounds: validate.maxRounds,
      minDaysToExpire: validate.minDaysToExpire,
      max30DaysToExpire: validate.max30DaysToExpire,
      notEmpty: validate.notEmpty,
      minDepositBondMultiplier: validate.minDepositBondMultiplier,
      maxDepositBondMultiplier: validate.maxDepositBondMultiplier,
      noSpecialCharactersAllowed: validate.noSpecialCharactersAllowed,
    },
    errors: {
      timeStart: {
        status: false,
        message: "",
      },
      timeFinal: {
        status: false,
        message: "",
      },
    },
    timeoutId: 0,
  }),

  created() {
    this.setInitialFields();
  },

  computed: {
    computedTitleCreateOrUpdateCampaign() {
      if (this.update) {
        return this.$t("freespins.form.subheaders.editYourCampaign");
      }

      return this.$t("freespins.form.subheaders.createYourCampaign");
    },

    isCreating() {
      return !this.update && !this.view;
    },

    disableButton() {
      if (this.update) {
        if (Object.keys(this.initialUpdatingFreespinState).length === 4) {
          const {
            campaignTitle,
            description,
            campaignHide,
            selectFreespinsProgress
          } = this.initialUpdatingFreespinState;
          const nothingChanged =
            campaignTitle === this.form.campaignTitle &&
            description === this.form.description &&
            campaignHide === this.form.campaignHide &&
            selectFreespinsProgress === this.form.selectFreespinsProgress.value;
          return nothingChanged;
        }
      }

      return (
        !this.form.valid ||
        this.loading.freespinCreation ||
        this.view ||
        !this.imageSent
      );
    },

    gamesByProviders() {
      return this.options.games.filter(
        (game) => game.provider === this.form.provider.provider
      );
    },

    formattedStartDate() {
      return this.formatDate(this.form.dates.start);
    },

    formattedFinalDate() {
      return this.formatDate(this.form.dates.final);
    },

    today() {
      return new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .substr(0, 10);
    },

    currentHourAndMinute() {
      const currentDate = new Date();

      let currentHour = currentDate.getHours();
      let currentMinute = currentDate.getMinutes();

      if (currentHour < 10) {
        currentHour = "0" + currentHour;
      }

      if (currentMinute < 10) {
        currentMinute = "0" + currentMinute;
      }

      return `${currentHour}:${currentMinute}`;
    },

    gameHint() {
      if (!this.form.provider || Object.keys(this.form.provider).length === 0) {
        return this.$t("freespins.form.selectAProvider");
      }

      return "";
    },

    creatingFreespin() {
      return !this.update && !this.view;
    },

    isCampaignTypeDeposit() {
      return (
        this.form.campaignType == "deposito" ||
        this.form.campaignType == "1ºdeposito"
      );
    },

    campaignHideLabel() {
      return this.$t("freespins.form.campaignVisibility.hide");
    },

    operator_name() {
      return this.$operator.operator_name;
    },

    configOneOrManyGamesByProviders() {
      return 1;

      // const providersAbleManyGames = [
      //   "belatra", // Belatra
      //   "softswiss", // BGAMING
      //   "evolution", // Evolution Gaming
      //   "kagaming", // Ka gaming
      //   "onlyplay", // OnlyPlay
      //   "pragmaticexternal", // Pragmatic Play Live
      // ];

      // return providersAbleManyGames
      //   .includes(this.form.provider.provider) ? 8 : 1;
    },

    hasDepositBond() {
      return (
        this.$operator &&
        this.isCampaignTypeDeposit
      );
    },
  },

  watch: {
    "form.games"(games) {
      if (games.length > 0) {
        this.options.games = [...games, ...this.options.games];
      }
    },
    "form.selectedPlayers"(newVal, oldVal) {
      try {
        this.changedListItemIndex = this.getChangedListItemIndex(
          newVal,
          oldVal
        );
      } catch {
        this.changedListItemIndex = -1;
      }

      if (!this.$refs.autoComplete) {
        return;
      }

      this.$refs.autoComplete.setMenuIndex(this.changedListItemIndex);

      if (this.multiple) {
        this.$nextTick(() => {
          this.$refs.autoComplete.setMenuIndex(this.changedListItemIndex);
        });
      } else {
        setTimeout(() => {
          this.$refs.autoComplete.setMenuIndex(this.changedListItemIndex);
        }, 100);
      }
    },

    "form.playerSearch"(val) {
      if (!val) {
        return;
      }

      clearTimeout(this.timeoutId);

      this.timeoutId = setTimeout(() => {
        this.fetchPlayersDebounced(val);
      }, 1000);
    },

    dialog(val) {
      if (val) {
        if (this.update || this.view) {
          this.fillFormWithFreespin(this.freespin);
          return;
        }

        if (this.options.providers.length === 0) {
          this.fetchProviders();
        }
      }
      val == false && this.clearCsvInput();
    },

    "form.dates.start"(val) {
      if (this.form.dates.final && val > this.form.dates.final) {
        this.form.dates.final = val;
      }
    },

    "form.provider"() {
      if (this.update || this.view) {
        return;
      }

      this.form.games = [];
    },

    "form.dates.timeStart"(val) {
      if (val.length == 5) {
        this.timeValidated(val, "start");
      } else {
        this.errors.timeStart.message = this.$t("invalidTime");
        this.errors.timeStart.status = true;
      }
    },

    "form.dates.timeFinal"(val) {
      if (val.length == 5) {
        this.timeValidated(val, "final");
      } else {
        this.errors.timeFinal.message = this.$t("invalidTime");
        this.errors.timeFinal.status = true;
      }
    },
  },

  methods: {
    closeDialog() {
      this.clearCsvInput();
      this.reset();
      this.dialog = false;
    },
    clearCsvInput(){
      this.form.csv.file = "";
      this.form.csv.seeCsvUsers = false;
      this.form.csv.parsed = false;
      this.form.csv.fileError = false;
      this.form.csv.content = [];
      this.form.csv.usersByCsv = [];
    },
    parseFile() {
      this.form.csv.fileError = false;
      this.$papa.parse(this.form.csv.file, {
        header: true,
        skipEmptyLine: true,
        complete: function(results) {
          this.form.csv.fileError = false;

          this.form.csv.content = results.data.flatMap((user) => {
            const id = user.id || user.Id || user.iD || user["ID"];
            if (id) {
              return { ...user, id };
            } else {
              return [];
            }
          });

          this.form.csv.usersByCsv = results.data.flatMap((user) => {
            const id = user.id || user.Id || user.iD || user["ID"];
            return id ? id : []
          });

          if (this.form.csv.usersByCsv.length == 0) {
            this.form.csv.content = [];
            this.form.csv.parsed = false;
            this.form.csv.fileError = true;
            this.$store.commit("setFeedbackSnack", {
              msg: this.$t("freespins.form.idColumnEmpty"),
              color: "warning",
            })

            return
          }
          this.form.csv.parsed = true;
        }.bind(this),
        error: function(error, file) {
          console.log(error, file);
        }.bind(this)
      });
    },

    handleCSVUpload(event) {
      if (!event) return;
      this.form.csv.file = event;
      if (this.form.csv.file.type !== "text/csv") {
        this.form.csv.file = "";
        this.form.csv.content = [];
        this.form.csv.parsed = false;
        this.form.csv.fileError = true;
        this.$store.commit("setFeedbackSnack", {
          msg: this.$t("freespins.form.fileNotAllowed"),
          color: "warning"
        })
        return
      }
      this.parseFile();
    },

    limitSelectedGames() {
      if (this.form.games.length > this.configOneOrManyGamesByProviders)
        this.form.games.pop();
    },
    createOrUpdateFreespin() {
      this.loading.freespinCreation = true;

      if (this.update) {
        const body = {
          campaign_freespins_id: this.freespin.campaign_id,
          campaign_title: this.form.campaignTitle,
          description: this.form.description,
          campaign_hide: this.form.campaignHide ? "1" : "0",
        };

        if (
          this.form.selectFreespinsProgress.value != false &&
          this.isCampaignTypeDeposit
        ) {
          body.progressive = this.form.selectFreespinsProgress.value;
        }

        this.$axios
          .post(
            `${this.$prefix()}/cassino/gamesAgregation/editFreespinsCampaign`,
            body,
            {
              headers: { "Content-Type": "application/json" },
            }
          )
          .then((res) => {
            this.$store.commit("setFeedbackSnack", {
              msg: res.data.messages,
              color: "success",
            });

            this.reset();
            this.closeDialog();
            this.$emit("fetchFreespins");
          })
          .finally(() => {
            this.loading.freespinCreation = false;
          });

        return;
      }

      let users_can_claim = this.form.players;
      console.log(users_can_claim);

      if (this.form.campaignType == "deposito") {
        users_can_claim = 5;
      } else if (this.form.campaignType == "1ºdeposito") {
        users_can_claim = 6;
      } else if (this.form.players === 5) {
        users_can_claim = 0;
      }

      const body = {
        campaign_title: this.form.campaignTitle,
        campaign_code: this.form.campaignCode,
        description: this.form.description,
        campaign_type: this.form.campaignType,
        min_deposit: this.isCampaignTypeDeposit
          ? this.form.minDeposit
          : "custom",
        campaign_hide:
          this.isCampaignTypeDeposit && this.form.campaignHide ? "1" : "0",
        users_can_claim,
        round_amount: this.form.rounds,
        provider_id: this.form.provider.provider_id,
        start_date: `${this.form.dates.start} ${this.form.dates.timeStart}:00`,
        final_date: `${this.form.dates.final} ${this.form.dates.timeFinal}:00`,
        ids_game: this.form.games.map((game) => game.game_id),
        expiration_days: this.form.daysToExpire,
        users: [],
      };

      if (this.hasDepositBond) {
        body.deposit_bond_multiplier = Number(this.form.multiplierBondDeposit);
      }
      if (
        this.form.selectFreespinsProgress.value != false &&
        this.isCampaignTypeDeposit
      ) {
        body.progressive = this.form.selectFreespinsProgress.value;
      }
      body.rules = rules(body, this.operator_name);
      body.functioning = functioning(body);

      if (this.form.campaignType == "personalizado") {
        // checar se add por login ou nome || cvs
        if (this.form.players === 5) {
          body.users = this.form.csv.usersByCsv;
        } else {
          this.form.selectedPlayers.forEach((item) => {
            body.users.push(item.id);
          });
        }
      }

      this.$axios
        .post(
          `${this.$prefix()}/cassino/gamesAgregation/createFreespinsCampaign`,
          body,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        )
        .then((res) => {
          this.$store.commit("setFeedbackSnack", {
            msg: res.data.messages,
            color: "success",
          });

          this.reset();
          this.closeDialog();
          this.$emit("fetchFreespins", true);
          // resetando imageSent após sucesso na criação da promo;
          this.imageSent = false;
        })
        .finally(() => {
          this.loading.freespinCreation = false;
        });
    },

    listFreespinsUsersByCampaignId() {
      const fetchedPlayers = this.fetchedPlayers
        ? this.fetchedPlayers[this.freespin.campaign_id]
        : false;

      if (fetchedPlayers) {
        this.form.selectedPlayers = fetchedPlayers;
        this.form.fetchedPlayers = fetchedPlayers;
        return;
      }

      const endpoint = `${this.$prefix()}/cassino/gamesAgregation/listFreespinsUsersByCampaignId`;

      const body = {
        campaign_freespins_id: this.freespin.campaign_id,
      };

      this.loading.playerSearch = true;

      this.$axios
        .post(endpoint, this.$formdata(body))
        .then((res) => {
          const players = res.data.dados.map((user) => ({
            ui: `${user.user_name} (${user.user_id})`,
            name: user.user_name,
            login: user.user_login,
            id: user.user_id,
          }));

          this.form.selectedPlayers = players;
          this.form.fetchedPlayers = players;

          this.$emit("updateFreespinFetchedPlayers", {
            campaign_id: this.freespin.campaign_id,
            players,
          });
        })
        .finally(() => {
          this.loading.playerSearch = false;
        });
    },

    fetchProviders() {
      if (this.$store.state.operator.casino == "0") {
        return;
      }

      this.loading.providers = true;

      this.$axios
        .post(
          `/${this.$prefix()}/cassino/gamesAgregation/listFreespinsAllowedGames`
        )
        .then((res) => {
          this.options.providers = res.data.dados;
          this.options.games = res.data.dados.map((game) => ({
            ...game,
            src: this.getGameImage(game),
          }));
        })
        .finally(() => {
          this.loading.providers = false;
        });
    },

    reset() {
      setTimeout(() => {
        this.form.campaignTitle = "";
        this.form.description = "";
        this.form.campaignType = "personalizado";
        this.form.campaignCode = "";
        this.form.minDeposit = "";
        this.form.campaignHide = false;
        this.form.players = "";
        this.form.selectedPlayers = [];
        this.form.fetchedPlayers = [];
        this.form.provider = {};
        this.form.games = [];
        this.form.rounds = "";
        this.form.daysToExpire = "";
        this.form.dates.start = this.today;
        this.form.dates.final = "";
        this.form.dates.timeStart = this.currentHourAndMinute;
        this.form.dates.timeFinal = "23:59";
        this.form.multiplierBondDeposit = "";
        this.errors.timeStart.status = false;
        this.errors.timeStart.message = "";
        this.errors.timeFinal.status = false;
        this.errors.timeFinal.message = "";

        if (this.$refs.form) {
          this.$refs.form.resetValidation();
        }
      }, 100);
    },

    setInitialFields() {
      setTimeout(() => {
        this.form.dates.start = this.today;
        this.form.dates.timeStart = this.currentHourAndMinute;
        this.form.dates.timeFinal = "23:59";
        this.form.campaignType = this.options.campaignTypes[0].value;
        this.form.selectFreespinsProgress = this.form.freespinsProgressItems[0].value;
      }, 300);
    },

    formatDate(date) {
      if (!date) return null;
      const [year, month, day] = date.split("-");
      return `${day}/${month}/${year}`;
    },

    getGameImage({ provider, identifier }) {
      return encodeURI(
        `https://cdn.softswiss.net/i/s3/${provider}/${
          identifier.split(":")[1]
        }.png`.replace(/\s/g, "")
      );
    },

    timeValidated(time, moment) {
      const splitted = time.split(":");
      const [hour, minute] = splitted;

      if (hour > 23 || minute > 59) {
        if (moment == "start") {
          this.errors.timeStart.message = this.$t("invalidTime");
          this.errors.timeStart.status = true;
          return;
        } else {
          this.errors.timeFinal.message = this.$t("invalidTime");
          this.errors.timeFinal.status = true;
        }
      } else {
        this.errors.timeStart.message = "";
        this.errors.timeStart.status = false;
        this.errors.timeFinal.message = "";
        this.errors.timeFinal.status = false;
      }
    },

    fillFormWithFreespin(freespin) {
      if (freespin.user_selected == "5") {
        this.form.campaignType = "deposito";
        this.form.multiplierBondDeposit = freespin.deposit_bond_multiplier;
      } else if (freespin.user_selected == "6") {
        this.form.campaignType = "1ºdeposito";
        this.form.multiplierBondDeposit = freespin.deposit_bond_multiplier;
      } else {
        this.form.campaignType = "personalizado";

        this.form.players = this.options.players.find(
          (option) => option.value == freespin.user_selected
        ).value;

        if (freespin.user_selected == "0") {
          this.listFreespinsUsersByCampaignId();
        }
      }

      const [startDate, startHour] = freespin.originalInitialDate.split(" ");
      const [endDate, finalHour] = freespin.originalFinalDate.split(" ");

      this.form.campaignTitle = freespin.title;
      this.form.description = freespin.description;
      // this.form.campaignType = freespin.type || 0;
      this.form.campaignCode = freespin.campaign_code || "";
      this.form.minDeposit = freespin.min_deposit || "";
      this.form.campaignHide = freespin.campaign_hide == 1 ? true : false;
      this.form.selectFreespinsProgress = freespin.progressive_spins == false ?
        { text: this.$t("disabled"), value: false }
        : Number(freespin.progressive_spins);

      this.initialUpdatingFreespinState = {
        campaignTitle: freespin.title,
        description: freespin.description,
        campaignHide: freespin.campaign_hide == 1 ? true : false,
        selectFreespinsProgress: freespin.progressive_spins == false ?
        { text: this.$t("disabled"), value: false }
        : Number(freespin.progressive_spins),
      };

      // Código para buscar o nome do provedor, buscando o primeiro pois só há
      // seleção de um provedor na criação
      this.form.provider = { provider: freespin.games[0].provider_name };

      this.form.rounds = freespin.quantity_of_round;

      this.form.daysToExpire = freespin.expiration_days;

      this.form.dates.start = startDate;
      this.form.dates.final = endDate;
      this.form.dates.timeStart = startHour.slice(0, -3);
      this.form.dates.timeFinal = finalHour.slice(0, -3);

      this.errors.timeStart.status = false;
      this.errors.timeStart.message = "";
      this.errors.timeFinal.status = false;
      this.errors.timeFinal.message = "";
    },

    fetchPlayersDebounced(val) {
      if (val === this.lastSearchedTerm) {
        return;
      }

      if (val.length < 3) {
        return;
      }

      const currentSelectedPlayers = [...this.form.selectedPlayers];
      this.loading.playerSearch = true;
      this.lastSearchedTerm = val;

      this.$axios
        .post(
          `${this.$prefix()}/players/search`,
          this.$formdata({
            term: val,
            online: 1,
          })
        )
        .then((res) => {
          const data = res.data.dados.flatMap((item) => {
            if (item.active === "1") {
              return [
                {
                  ui: `${item.nome} (${item.id})`,
                  name: item.nome,
                  icon: item.online,
                  login: item.login,
                  id: item.id,
                },
              ];
            }

            return [];
          });

          this.form.fetchedPlayers = [];
          this.form.fetchedPlayers = [...data, ...currentSelectedPlayers];
        })
        .finally(() => {
          this.loading.playerSearch = false;
        });
    },

    getChangedListItemIndex(newVal, oldVal) {
      let lastChangedItem;
      if (this.multiple && newVal.length > oldVal.length) {
        lastChangedItem = newVal[newVal.length - 1];
      } else if (this.multiple) {
        lastChangedItem = oldVal.find((x) => !newVal.includes(x));
      } else {
        lastChangedItem = newVal;
      }

      let listItemValue;
      if (this.returnObject) {
        listItemValue = lastChangedItem[this.itemValue];
      } else {
        listItemValue = lastChangedItem;
      }

      return this.items.findIndex(
        (item) => item[this.itemValue] === listItemValue
      );
    },

    isImageSent() {
      this.imageSent = !this.imageSent;
    },

    campaignCodeImgCustom(campaignCode) {
      this.form.campaignCode = campaignCode;
    },
  },
};
</script>
<style>
.v-autocomplete .v-chip--select {
  margin: 0.25rem !important;
}

.v-autocomplete .v-select__selections {
  max-height: 120px;
  overflow-y: auto;
}

.tooltip {
  width: 260px !important;
}
</style>
